import type { AppEvent, AppEventProperty } from "./events";

type AnalyticsValue = string | number | boolean | null;

/**
 * Narrow on purpose: anything reported is reduced to a scalar at the call site,
 * so a draft, claim, or API response cannot reach a third party by being in
 * scope.
 */
export type AnalyticsProperties = Record<string, AnalyticsValue>;

/**
 * What an event may carry, by the approved list, so a property the list does
 * not name fails to compile rather than being dropped quietly on the way out.
 */
export type EventProperties<E extends AppEvent> = [
  AppEventProperty<E>,
] extends [never]
  ? never
  : Partial<Record<AppEventProperty<E>, AnalyticsValue>>;

/**
 * Implemented by the PostHog adapter, and by the do-nothing client below when
 * no project key is configured, so callers never ask whether analytics is on.
 */
export interface AnalyticsClient {
  /** Only an event on the approved list in `events.ts`, with its properties. */
  capture: <E extends AppEvent>(
    event: E,
    properties?: EventProperties<E>,
  ) => void;
  identify: (distinctId: string, properties?: AnalyticsProperties) => void;
  /** Properties every later event carries, until the next reset. */
  register: (properties: AnalyticsProperties) => void;
  reset: () => void;

  /** Report something that went wrong, with whatever context is known. */
  captureException: (error: Error, properties?: AnalyticsProperties) => void;
}

export const disabledAnalytics: AnalyticsClient = {
  capture() {},
  identify() {},
  register() {},
  reset() {},
  captureException() {},
};
